/**
 * Helper functions for the graph application
 */

// Generate a unique ID for new nodes
export const generateUniqueId = () => {
  return `node_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
};

// Format node data based on type and operation
export const formatNodeData = (type, operation, content) => {
  const baseData = {
    title: `${operation.charAt(0).toUpperCase() + operation.slice(1)} Result`,
    outputs: ['Out 1', 'Out 2', 'Out 3']
  };

  if (type === 'textNode') {
    return {
      ...baseData,
      content
    };
  } else if (type === 'imageNode') {
    return {
      ...baseData,
      imageUrl: content
    };
  }

  return baseData;
};

/**
 * Predict the desired output modality from a prompt string + optional parent node type.
 * Returns 'image' | 'text'.
 *
 * Heuristics (in priority order):
 *  1. Explicit image-output keywords in prompt → 'image'
 *  2. Parent is imageNode/fileNode + prompt is a transformation → 'image'
 *  3. Default → 'text'
 */
export const predictOutputModality = (promptText = '', parentType = null) => {
  const p = promptText.toLowerCase();

  // Strong image-intent signals
  const imageSignals = [
    /\b(generate|create|make|produce|draw|render|paint|design|illustrate|show me|give me|i (need|want|require))\b.*\b(image|photo|picture|illustration|artwork|painting|drawing|graphic|visual|portrait|landscape|scene|poster|logo|icon)\b/,
    /\b(image|photo|picture|illustration|artwork|painting|drawing|graphic|visual)\b.*\b(of|showing|depicting|with|in the style)\b/,
    /\bin the style of\b/,
    /\b(photorealistic|cinematic|anime|watercolor|oil painting|pixel art|graffiti|street art|3d render|digital art)\b/,
    /\bvisualiz(e|ation)\b/,
  ];

  for (const re of imageSignals) {
    if (re.test(p)) return 'image';
  }

  // If parent is already an image/file and prompt sounds like a visual transformation
  if (parentType === 'imageNode' || parentType === 'fileNode') {
    const transformSignals = [
      /\b(convert|transform|turn|make|change|render|apply|add|remove|style)\b/,
      /\b(background|color|filter|effect|style|art|painting|cartoon|sketch)\b/,
    ];
    for (const re of transformSignals) {
      if (re.test(p)) return 'image';
    }
  }

  return 'text';
};

/**
 * Extract a compact schema/preview string from raw file text.
 * Used to populate data.schema_preview on fileNodes.
 *
 * CSV   → "CSV: columns [A, B, C] | sample: row1val, row2val"
 * JSON  → "JSON: keys [a, b, c] | types: {a: string, b: number}"
 * text  → first 200 chars
 */
export const extractFileSchema = (rawText = '', mimeType = '') => {
  const text = rawText.trimStart();

  // CSV / TSV
  const isCSV = mimeType.includes('csv') || mimeType.includes('tab-separated') ||
    mimeType === 'text/plain' && text.includes(',');
  if (isCSV || mimeType.includes('csv')) {
    const lines = text.split(/\r?\n/).filter(Boolean);
    if (lines.length === 0) return '';
    const headers = lines[0].split(',').map((h) => h.trim().replace(/^["']|["']$/g, ''));
    const sample = lines.slice(1, 3).map((l) =>
      l.split(',').slice(0, 4).map((v) => v.trim().replace(/^["']|["']$/g, '')).join(', ')
    );
    return `CSV: columns [${headers.slice(0, 10).join(', ')}${headers.length > 10 ? ', …' : ''}]` +
      (sample.length ? ` | rows: ${sample.join(' / ')}` : '');
  }

  // JSON
  if (mimeType.includes('json') || text.startsWith('{') || text.startsWith('[')) {
    try {
      const parsed = JSON.parse(text.slice(0, 4000));
      const obj = Array.isArray(parsed) ? parsed[0] : parsed;
      if (obj && typeof obj === 'object') {
        const keys = Object.keys(obj).slice(0, 10);
        const types = keys.map((k) => `${k}:${typeof obj[k]}`).join(', ');
        return `JSON${Array.isArray(parsed) ? ` (${parsed.length} items)` : ''}: {${types}${Object.keys(obj).length > 10 ? ', …' : ''}}`;
      }
    } catch {}
  }

  // Plain text / markdown
  return text.slice(0, 200).replace(/\s+/g, ' ');
};

// Extract meaningful content from an API response object
// Falls back to known keys or the first non-action key
export const extractContentFromResponse = (operation, response) => {
  if (!response || typeof response !== 'object') {
    return '';
  }

  // If the API already returns `content`, use it directly
  if (response.content) {
    return response.content;
  }

  // Convert operation names like "short-form" to camelCase "shortForm"
  const camelOp = operation.replace(/[-_](.)/g, (_, c) => c.toUpperCase());

  // Check a few common key variations
  const possibleKeys = [operation, camelOp, `${camelOp}Text`, 'expanded', 'summary', 'shortForm'];
  for (const key of possibleKeys) {
    if (response[key]) {
      return response[key];
    }
  }

  // As a last resort, return the value of the only non-action key if present
  const keys = Object.keys(response).filter((k) => k !== 'action');
  if (keys.length === 1) {
    return response[keys[0]];
  }

  return '';
};
